import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    ArrowLeft,
    Save,
    User,
    MapPin,
    Star,
    MessageSquare,
    Shield,
    Heart,
    Tag,
    CheckCircle,
    XCircle,
    Clock
} from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useUpdateTestimonialMutation, useGetTestimonialsQuery } from '../../store/slices/apiSlice';

const TestimonialValidationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    text: Yup.string().required('Testimonial text is required'),
    rating: Yup.number().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5').required('Rating is required'),
    location: Yup.string().required('Location is required'),
    profile: Yup.string().url('Must be a valid URL').required('Profile URL is required'),
    category: Yup.string().required('Category is required'),
    status: Yup.string().oneOf(['pending', 'approved', 'rejected']).required('Status is required'),
    verified: Yup.boolean().required(),
    featured: Yup.boolean().required(),
    helpful: Yup.number().min(0).required('Helpful count is required'),
});

interface FormValues {
    name: string;
    text: string;
    rating: number;
    location: string;
    profile: string;
    category: string;
    status: 'pending' | 'approved' | 'rejected';
    verified: boolean;
    featured: boolean;
    helpful: number;
}

const EditTestimonial = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [updateTestimonial, { isLoading: isUpdating }] = useUpdateTestimonialMutation();
    const { data: testimonialsData, isLoading: isLoadingTestimonials } = useGetTestimonialsQuery();

    const [testimonial, setTestimonial] = useState<any>(null);

    useEffect(() => {
        if (testimonialsData?.data?.testimonials && id) {
            const foundTestimonial = testimonialsData.data.testimonials.find(t => t.id === parseInt(id));
            if (foundTestimonial) {
                setTestimonial(foundTestimonial);
            } else {
                toast.error('Testimonial not found');
                navigate('/admin/testimonials');
            }
        }
    }, [testimonialsData, id, navigate]);

    const formik = useFormik<FormValues>({
        initialValues: {
            name: testimonial?.name || '',
            text: testimonial?.text || '',
            rating: testimonial?.rating || 5,
            location: testimonial?.location || '',
            profile: testimonial?.profile || '',
            category: testimonial?.category || 'laundry',
            status: testimonial?.status || 'approved',
            verified: testimonial?.verified || true,
            featured: testimonial?.featured || false,
            helpful: testimonial?.helpful || 0,
        },
        enableReinitialize: true,
        validationSchema: TestimonialValidationSchema,
        onSubmit: async (values) => {
            if (!id) {
                toast.error('Testimonial ID is missing');
                return;
            }

            try {
                console.log('Updating testimonial with data:', { id: parseInt(id), ...values });
                
                const result = await updateTestimonial({
                    id: parseInt(id),
                    ...values
                }).unwrap();
                console.log('Testimonial updated successfully:', result);
                
                toast.success('Testimonial updated successfully!');
                navigate('/admin/testimonials');
            } catch (error: any) {
                console.error('Failed to update testimonial:', error);
                
                if (error?.status === 400) {
                    toast.error('Bad Request: Please check your form data and try again.');
                } else if (error?.status === 401) {
                    toast.error('Unauthorized: Please log in again.');
                } else if (error?.status === 404) {
                    toast.error('Testimonial not found.');
                } else {
                    toast.error('Failed to update testimonial. Please try again.');
                }
            }
        },
    });

    const getFieldError = (fieldName: keyof FormValues) => {
        return formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : '';
    };

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`h-5 w-5 ${
                    index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                }`}
            />
        ));
    };

    if (isLoadingTestimonials) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading testimonial...</p>
                </div>
            </div>
        );
    }

    if (!testimonial) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <p className="text-gray-600">Testimonial not found</p>
                    <button
                        onClick={() => navigate('/admin/testimonials')}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Testimonials
                    </button>
                </div>
            </div>
        );
    }

    if (isUpdating) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Updating testimonial...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Testimonial</h1>
                        <p className="text-gray-600">Update testimonial details for {testimonial.name}</p>
                    </div>
                    <button
                        onClick={() => navigate('/admin/testimonials')}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Testimonials
                    </button>
                </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <form onSubmit={formik.handleSubmit} className="p-8 space-y-8">
                    {/* Customer Information */}
                    <div className="border-b border-gray-200 pb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <User className="h-5 w-5 mr-2" />
                            Customer Information
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                    Customer Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    placeholder="Enter customer name"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        getFieldError('name') ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                                    }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.name}
                                />
                                {getFieldError('name') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('name')}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                                    Location <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                    <input
                                        type="text"
                                        id="location"
                                        name="location"
                                        placeholder="Enter location (e.g., New York, NY)"
                                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                            getFieldError('location') ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                                        }`}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.location}
                                    />
                                </div>
                                {getFieldError('location') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('location')}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="profile" className="block text-sm font-medium text-gray-700 mb-2">
                                    Profile URL <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    id="profile"
                                    name="profile"
                                    placeholder="https://example.com/profile.jpg"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        getFieldError('profile') ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                                    }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.profile}
                                />
                                {getFieldError('profile') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('profile')}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="category"
                                    name="category"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        getFieldError('category') ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                                    }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.category}
                                >
                                    <option value="laundry">Laundry</option>
                                    <option value="investment">Investment</option>
                                    <option value="premium">Premium</option>
                                    <option value="general">General</option>
                                </select>
                                {getFieldError('category') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('category')}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Testimonial Content */}
                    <div className="border-b border-gray-200 pb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <MessageSquare className="h-5 w-5 mr-2" />
                            Testimonial Content
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-2">
                                    Testimonial Text <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    id="text"
                                    name="text"
                                    placeholder="Enter the customer's testimonial..."
                                    rows={4}
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors resize-none ${
                                        getFieldError('text') ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                                    }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.text}
                                />
                                {getFieldError('text') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('text')}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Rating <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center space-x-2">
                                    {renderStars(formik.values.rating)}
                                    <span className="ml-2 text-sm text-gray-600">({formik.values.rating}/5)</span>
                                </div>
                                <input
                                    type="range"
                                    id="rating"
                                    name="rating"
                                    min="1"
                                    max="5"
                                    step="1"
                                    className="w-full mt-2"
                                    onChange={formik.handleChange}
                                    value={formik.values.rating}
                                />
                                {getFieldError('rating') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('rating')}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="helpful" className="block text-sm font-medium text-gray-700 mb-2">
                                    Helpful Votes
                                </label>
                                <input
                                    type="number"
                                    id="helpful"
                                    name="helpful"
                                    min="0"
                                    placeholder="0"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        getFieldError('helpful') ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                                    }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.helpful}
                                />
                                {getFieldError('helpful') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('helpful')}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Status & Settings */}
                    <div className="border-b border-gray-200 pb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <Shield className="h-5 w-5 mr-2" />
                            Status & Settings
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="status"
                                    name="status"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                        getFieldError('status') ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                                    }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.status}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                                {getFieldError('status') && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError('status')}</p>
                                )}
                            </div>

                            <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="verified"
                                        checked={formik.values.verified}
                                        onChange={formik.handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Verified Customer</span>
                                </label>
                            </div>

                            <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="featured"
                                        checked={formik.values.featured}
                                        onChange={formik.handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Featured Testimonial</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4 pt-6">
                        <button
                            type="button"
                            onClick={() => navigate('/admin/testimonials')}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
                        >
                            <Save className="h-4 w-4 mr-2" />
                            {isUpdating ? 'Updating...' : 'Update Testimonial'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditTestimonial;
